import { createSlice } from '@reduxjs/toolkit';
import {
  request,
  generateCancelToken,
  cancelRequests as cancelHttpRequests,
  isCancel,
  ejectCancelInterceptor,
} from 'modules/Api/HttpClient';
import { formatDateToSubmit } from 'modules/Utils/Date';

let cancelToken;

const initialState = {
  loading: false,
  error: null,
  itemTypes: [],
  data: {
    page: 1,
    perPage: 25,
    date_from: '',
    date_to: '',
    event: '',
    item_type: '',
    whodunnit: '',
    total: 0,
    audit_logs: [],
  },
  detail: null,
};

const auditLogsSlice = createSlice({
  name: 'auditLogs',
  initialState,
  reducers: {
    cancelRequests: () => {
      cancelToken?.cancel();
      cancelHttpRequests();
    },
    cleanState: () => ({ ...initialState }),
    requestAuditLogs: (state) => {
      state.loading = true;
      state.error = null;
    },
    receiveAuditLogs: (state, action) => {
      state.loading = false;
      state.data.total = action.payload.total_items;
      state.data.audit_logs = action.payload.audit_logs;
    },
    receiveAuditLogsError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    receiveItemTypes: (state, action) => {
      state.itemTypes = action.payload.item_types || [];
    },
    setDetail: (state, action) => {
      state.detail = action.payload;
    },
    clearDetail: (state) => {
      state.detail = null;
    },
    changeFilters: (state, action) => {
      state.data.page = action.payload.page ? Number(action.payload.page) : 1;
      state.data.date_from = action.payload.date_from || '';
      state.data.date_to = action.payload.date_to || '';
      state.data.event = action.payload.event || '';
      state.data.item_type = action.payload.item_type || '';
      state.data.whodunnit = action.payload.whodunnit || '';
    },
    changePage: (state, action) => {
      state.data.page = action.payload;
    },
    changePerPage: (state, action) => {
      state.data.perPage = action.payload;
      state.data.page = 1;
    },
    clearFilters: (state) => {
      state.data.page = 1;
      state.data.date_from = '';
      state.data.date_to = '';
      state.data.event = '';
      state.data.item_type = '';
      state.data.whodunnit = '';
    },
  },
});

const Actions = auditLogsSlice.actions;

const Selectors = {
  fetchState: (state) => state.auditLogs,
  loading: ({ auditLogs: { loading } }) => ({ loading }),
};

const Async = {
  fetchAuditLogs: () => async (dispatch, getState) => {
    const {
      auditLogs: {
        data: {
          page,
          perPage,
          date_from,
          date_to,
          event,
          item_type,
          whodunnit,
        },
      },
    } = getState();

    ejectCancelInterceptor();
    cancelToken?.cancel();
    cancelToken = generateCancelToken();

    dispatch(Actions.requestAuditLogs());

    let action;

    try {
      const response = await request({
        cancelToken: cancelToken.token,
        method: 'GET',
        url: '/cms/v1/audit_logs',
        params: {
          page,
          per_page: perPage,
          date_from: formatDateToSubmit(date_from),
          date_to: formatDateToSubmit(date_to),
          event: event || undefined,
          item_type: item_type || undefined,
          whodunnit: whodunnit || undefined,
        },
      });

      action = Actions.receiveAuditLogs(response.data.content);
    } catch (e) {
      if (!isCancel(e)) {
        action = Actions.receiveAuditLogsError(e.message);
      }
    }

    action && dispatch(action);
  },

  fetchItemTypes: () => async (dispatch) => {
    try {
      const response = await request({
        method: 'GET',
        url: '/cms/v1/audit_logs/item_types',
      });
      dispatch(Actions.receiveItemTypes(response.data.content));
    } catch (e) {
      // non-critical, ignore
    }
  },
};

const { reducer } = auditLogsSlice;

export { reducer, Actions, Async, Selectors };
